import {
  getCard,
  getGroup,
  type CardDefinition,
  type GroupId,
  type PropertyGroup,
} from '@lafayette/content';
import type { CSSProperties, ReactNode } from 'react';

import styles from './Card.module.css';
import { describeGroups, GroupChip, patternClass } from './GroupChip.js';

export type CardSize = 'xs' | 'sm' | 'md' | 'lg';

/** Cards shrink with the viewport so a phone still fits a readable table. */
export const CARD_WIDTHS: Record<CardSize, string> = {
  xs: 'clamp(40px, 11vw, 58px)',
  sm: 'clamp(56px, 15vw, 84px)',
  md: 'clamp(104px, 25vw, 132px)',
  lg: 'min(78vw, 220px)',
};

/**
 * How much of a card fits legibly at each size. Anything a face cannot show
 * without clipping is deferred to the controls panel and the zoomed view.
 */
interface Detail {
  /** Property titles, which malls need in order to be read. */
  title: boolean;
  /** Action titles. A banked action is just money, so it shows its value. */
  actionTitle: boolean;
  /** The rent schedule, which players want on every property they can see. */
  rentTable: boolean;
  /** Drops the rent table's header row where there is no room for it. */
  compactRent: boolean;
  kicker: boolean;
  description: boolean;
}

const DETAIL: Record<CardSize, Detail> = {
  xs: {
    title: false,
    actionTitle: false,
    rentTable: false,
    compactRent: true,
    kicker: false,
    description: false,
  },
  sm: {
    title: true,
    actionTitle: false,
    rentTable: true,
    compactRent: true,
    kicker: false,
    description: false,
  },
  md: {
    title: true,
    actionTitle: true,
    rentTable: true,
    compactRent: false,
    kicker: true,
    description: false,
  },
  lg: {
    title: true,
    actionTitle: true,
    rentTable: true,
    compactRent: false,
    kicker: true,
    description: true,
  },
};

export interface CardProps {
  definitionId: string;
  size?: CardSize;
  /** Which face a wildcard is currently showing. */
  group?: GroupId;
  tenantName?: string;
  selected?: boolean;
  /** Marks the card as a legal target for the action in progress. */
  targetable?: boolean;
  /** Marks a public card another player has requested from its owner. */
  requested?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** Extra text appended to the accessible label, e.g. "in your bank". */
  context?: string;
  className?: string;
}

function groupStyle(group: PropertyGroup): CSSProperties {
  return {
    ['--band-color' as string]: group.color,
    ['--band-ink' as string]: group.onColor,
  };
}

/** Longer titles get smaller type so a face never has to clip one. */
function titleClass(text: string): string {
  if (text.length <= 8) return styles.titleShort!;
  if (text.length <= 16) return styles.titleMedium!;
  if (text.length <= 26) return styles.titleLong!;
  return styles.titleXLong!;
}

function RentTable({ group, compact }: { group: PropertyGroup; compact: boolean }): ReactNode {
  return (
    <div className={`${styles.rentTable} ${compact ? styles.rentTableCompact : ''}`}>
      {compact ? null : (
        <div className={styles.rentHead}>
          <span>Leased</span>
          <span>Rent</span>
        </div>
      )}
      {group.rent.map((amount, index) => {
        const owned = index + 1;
        const full = owned === group.size;
        return (
          <div key={owned} className={`${styles.rentRow} ${full ? styles.rentRowFull : ''}`}>
            <span>
              {owned}
              {full && !compact ? <span className={styles.fullLabel}> full</span> : null}
            </span>
            <span>${amount}M</span>
          </div>
        );
      })}
    </div>
  );
}

function PropertyFace({
  definition,
  detail,
  tenantName,
}: {
  definition: Extract<CardDefinition, { kind: 'property' }>;
  detail: Detail;
  tenantName?: string;
}): ReactNode {
  const group = getGroup(definition.group);
  return (
    <div className={styles.face}>
      <span className={styles.valueBadge}>${definition.value}M</span>
      <div
        className={`${styles.band} ${detail.rentTable ? '' : styles.bandOnly} ${patternClass(
          group.pattern,
        )}`}
        style={groupStyle(group)}
      >
        {detail.title ? (
          <span className={styles.bandTitle}>
            <span className={`${styles.title} ${titleClass(definition.title)}`}>
              {definition.title}
            </span>
            {tenantName ? <span className={styles.tenant}>{tenantName}</span> : null}
          </span>
        ) : null}
      </div>
      {detail.rentTable ? <RentTable group={group} compact={detail.compactRent} /> : null}
    </div>
  );
}

function WildcardFace({
  definition,
  detail,
  group,
  tenantName,
}: {
  definition: Extract<CardDefinition, { kind: 'propertyWildcard' }>;
  detail: Detail;
  group?: GroupId;
  tenantName?: string;
}): ReactNode {
  const rainbow = definition.groups.length > 2;
  const shown = group ?? definition.groups[0]!;
  const primary = getGroup(shown);
  const secondary = getGroup(definition.groups.find((g) => g !== shown) ?? shown);
  // The detail view always shows every color the card could be, even for one
  // already filling a mall — the stripes say it, so no chips are needed.
  const showsEveryColor = detail.description;
  // Once a wildcard is filling a mall it behaves like any other property there,
  // so it takes the same banded shape and the group's rent schedule.
  const inPlay = group !== undefined && detail.rentTable && !showsEveryColor;

  return (
    <div className={styles.face}>
      <span className={styles.valueBadge}>${definition.value}M</span>
      <div
        className={`${styles.band} ${inPlay ? '' : `${styles.bandOnly} ${styles.bandWild}`} ${
          inPlay ? '' : rainbow ? styles.bandRainbow : styles.bandSplit
        }`}
        style={{
          ...groupStyle(primary),
          ['--band-color-2' as string]: secondary.color,
        }}
      >
        {!detail.title ? null : inPlay ? (
          // Banded in a mall: the compact pill, with the invented tenant below.
          <span className={styles.bandTitle}>
            <span className={styles.wildPill}>Wildcard</span>
            {tenantName ? <span className={styles.tenant}>{tenantName}</span> : null}
          </span>
        ) : (
          // Full-bleed face: one bubble centred on the stripes.
          <span className={styles.wildBubble}>
            <span>Wildcard</span>
            {tenantName ? <span className={styles.wildTenant}>{tenantName}</span> : null}
          </span>
        )}
      </div>
      {inPlay ? <RentTable group={primary} compact={detail.compactRent} /> : null}
      {/* Exactly one of the bubble and the mark shows; CSS picks by card width. */}
      <span className={styles.wildMark} aria-hidden="true">
        W
      </span>
    </div>
  );
}

function MoneyFace({
  definition,
}: {
  definition: Extract<CardDefinition, { kind: 'money' }>;
}): ReactNode {
  return (
    <div className={`${styles.face} ${styles.money}`} style={tintStyle(definition.value)}>
      <span className={styles.valueBadge}>${definition.value}M</span>
      <span className={styles.moneyAmount}>${definition.value}M</span>
      <span className={`${styles.valueBadge} ${styles.valueBadgeEnd}`}>${definition.value}M</span>
    </div>
  );
}

function ActionFace({
  definition,
  detail,
}: {
  definition: Extract<CardDefinition, { kind: 'action' | 'rent' }>;
  detail: Detail;
}): ReactNode {
  const isRent = definition.kind === 'rent';
  return (
    <div className={`${styles.face} ${styles.action}`} style={tintStyle(definition.value)}>
      <div className={styles.actionFrame} />
      <span className={styles.valueBadge}>${definition.value}M</span>
      {detail.kicker ? (
        <span className={styles.kicker}>{isRent ? 'Rent Card' : 'Action Card'}</span>
      ) : null}
      {detail.actionTitle ? (
        <div
          className={`${styles.medallion} ${isRent ? styles.medallionRent : ''}`}
          style={isRent ? { background: ringConic(definition.groups) } : undefined}
        >
          {isRent ? (
            <span className={styles.rentInner}>RENT</span>
          ) : (
            <span className={`${styles.title} ${titleClass(definition.title)}`}>
              {definition.title}
            </span>
          )}
        </div>
      ) : (
        <span className={styles.moneyAmount} style={{ margin: 'auto' }}>
          ${definition.value}M
        </span>
      )}
      {detail.description ? <p className={styles.description}>{definition.description}</p> : null}
      <span className={`${styles.valueBadge} ${styles.valueBadgeEnd}`}>${definition.value}M</span>
    </div>
  );
}

function ringConic(groups: GroupId[]): string {
  if (groups.length === 0) return 'transparent';
  const step = 100 / groups.length;
  const stops = groups
    .map((id, i) => `${getGroup(id).color} ${i * step}% ${(i + 1) * step}%`)
    .join(', ');
  return `conic-gradient(${stops})`;
}

function tintStyle(value: number): CSSProperties {
  return { ['--tint' as string]: `var(--lf-value-${value}, var(--lf-paper-dim))` };
}

/** Human-readable label used by screen readers and by Playwright. */
export function describeCard(
  definition: CardDefinition,
  group?: GroupId,
  tenantName?: string,
): string {
  switch (definition.kind) {
    case 'money':
      return `${definition.title} money card`;
    case 'property':
      return `${definition.title}, ${getGroup(definition.group).label} property worth $${definition.value}M`;
    case 'propertyWildcard': {
      const shown = group
        ? `, filling ${getGroup(group).label}`
        : `, ${describeGroups(definition.groups)}`;
      const tenant = tenantName ? `, tenant ${tenantName}` : '';
      return `Property wildcard worth $${definition.value}M${shown}${tenant}`;
    }
    case 'rent':
      return `${definition.title} card worth $${definition.value}M, ${describeGroups(definition.groups)}`;
    case 'action':
      return `${definition.title} action card worth $${definition.value}M`;
  }
}

export function Card({
  definitionId,
  size = 'md',
  group,
  tenantName,
  selected = false,
  targetable = false,
  requested = false,
  disabled = false,
  onClick,
  context,
  className = '',
}: CardProps): ReactNode {
  const definition = getCard(definitionId);
  const detail = DETAIL[size];
  const label = `${describeCard(definition, group, tenantName)}${context ? `, ${context}` : ''}`;
  const style = { ['--w' as string]: CARD_WIDTHS[size] };

  const face = (() => {
    switch (definition.kind) {
      case 'property':
        return <PropertyFace definition={definition} detail={detail} tenantName={tenantName} />;
      case 'propertyWildcard':
        return (
          <WildcardFace
            definition={definition}
            detail={detail}
            group={group}
            tenantName={tenantName}
          />
        );
      case 'money':
        return <MoneyFace definition={definition} />;
      case 'action':
      case 'rent':
        return <ActionFace definition={definition} detail={detail} />;
    }
  })();

  const classes = [
    styles.card,
    selected ? styles.selected : '',
    targetable ? styles.targetable : '',
    requested ? styles.requested : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (!onClick) {
    return (
      <div className={classes} style={style} role="img" aria-label={label} data-card={definitionId}>
        {face}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${classes} ${styles.interactive}`}
      style={style}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={label}
      data-card={definitionId}
    >
      {face}
    </button>
  );
}

export function CardBack({
  size = 'md',
  label = 'Face-down card',
}: {
  size?: CardSize;
  label?: string;
}): ReactNode {
  // An empty label means the card is decorative, e.g. one of a fanned row that
  // is already summarised as "Ben holds 5 cards".
  const decorative = label.length === 0;
  return (
    <div
      className={`${styles.card} ${styles.back}`}
      style={{ ['--w' as string]: CARD_WIDTHS[size] }}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    >
      <span>
        <span className={styles.backTitle}>LAFAYETTE</span>
        <br />
        <span className={styles.backSub}>DEAL</span>
      </span>
    </div>
  );
}

export { GroupChip };
